import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Power, RotateCw, UserCog, ExternalLink, Search, Link as LinkIcon, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/contexts/AuthContext";
import MasterTenantCreateWizard from "@/pages/MasterTenantCreateWizard";
import TenantAccessLinks from "@/components/TenantAccessLinks";

const VERTICALS = [
  { v: "beleza", l: "Salão de Beleza" },
  { v: "barbearia", l: "Barbearia" },
  { v: "pet", l: "Pet" },
];

function StatusBadge({ s }) {
  const map = {
    active: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
    suspended: "bg-red-500/10 text-red-700 border-red-500/20",
    trial: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  };
  return <Badge variant="outline" className={map[s] || ""}>{s}</Badge>;
}

export default function MasterTenants() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [accessTenant, setAccessTenant] = useState(null); // tenant cujos links estão sendo exibidos
  const [deleteTarget, setDeleteTarget] = useState(null); // tenant que está prestes a ser excluído
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const auth = useAuth();
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master/tenants", { params: { q: search || undefined } });
      setList(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const suspend = async (id) => {
    try { await api.post(`/master/tenants/${id}/suspend`); toast.success("Empresa suspensa"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reactivate = async (id) => {
    try { await api.post(`/master/tenants/${id}/reactivate`); toast.success("Empresa reativada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openDelete = (t) => { setDeleteTarget(t); setConfirmText(""); };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (confirmText.trim().toLowerCase() !== deleteTarget.slug.toLowerCase()) {
      toast.error("O texto digitado não confere com o slug da empresa.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/master/tenants/${deleteTarget.id}`);
      toast.success(`Empresa "${deleteTarget.legal_name}" excluída.`);
      setDeleteTarget(null); setConfirmText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };
  const impersonate = async (t) => {
    try {
      await auth.impersonate(t.id);
      toast.success(`Acessando ${t.legal_name} como tenant_admin`);
      // Hard navigate forces a fresh AuthContext bootstrap with the new
      // impersonation token, avoiding the race between master-role
      // ProtectedRoute and the tenant-role re-fetch.
      window.location.assign(`/t/${t.slug}/dashboard`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-8 gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Empresas</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Empresas ({list.length})</h1>
          <p className="text-sm text-muted-foreground mt-1">Crie, suspenda, reative, exclua ou entre como administrador de uma empresa.</p>
        </div>
        <Button data-testid={TID.tenantsCreateBtn} className="gap-2" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4" /> Nova empresa
        </Button>
        <MasterTenantCreateWizard open={open} setOpen={setOpen} onCreated={load} />
      </div>

      <div className="mb-4 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="pl-9" placeholder="Buscar por slug ou razão social" />
      </div>

      <Card className="rounded-xl border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Carregando…</div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">Nenhuma empresa ainda. Crie a primeira.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={TID.tenantsTable}>
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Empresa</th>
                  <th className="text-left px-5 py-3 font-medium">Endereço</th>
                  <th className="text-left px-5 py-3 font-medium">Segmento</th>
                  <th className="text-left px-5 py-3 font-medium">Status</th>
                  <th className="text-right px-5 py-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} data-testid={TID.tenantRow(t.id)} className="border-t border-border hover:bg-muted/30">
                    <td className="px-5 py-3.5">
                      <div className="font-medium">{t.legal_name}</div>
                      <div className="text-xs text-muted-foreground">{t.branding?.display_name}</div>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs">{t.slug}</td>
                    <td className="px-5 py-3.5 capitalize">{t.vertical}</td>
                    <td className="px-5 py-3.5"><StatusBadge s={t.status} /></td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setAccessTenant(t)}
                          data-testid={`tenant-access-${t.id}`} className="gap-1.5 text-tenant">
                          <LinkIcon className="w-3.5 h-3.5" /> Acessos
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => nav(`/master/tenants/${t.id}`)}
                          data-testid={TID.tenantOpenDetail(t.id)} className="gap-1.5">
                          <ExternalLink className="w-3.5 h-3.5" /> Detalhes
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => impersonate(t)}
                          data-testid={TID.tenantImpersonateBtn(t.id)} className="gap-1.5">
                          <UserCog className="w-3.5 h-3.5" /> Entrar como
                        </Button>
                        {t.status === "suspended" ? (
                          <Button size="sm" variant="ghost" onClick={() => reactivate(t.id)}
                            data-testid={TID.tenantReactivateBtn(t.id)} className="gap-1.5 text-emerald-700">
                            <RotateCw className="w-3.5 h-3.5" /> Reativar
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => suspend(t.id)}
                            data-testid={TID.tenantSuspendBtn(t.id)} className="gap-1.5 text-destructive">
                            <Power className="w-3.5 h-3.5" /> Suspender
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openDelete(t)}
                          data-testid={`tenant-delete-${t.id}`} className="gap-1.5 text-destructive hover:bg-destructive/10"
                          title="Excluir empresa permanentemente"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Excluir
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal de acessos por empresa */}
      <Dialog open={!!accessTenant} onOpenChange={(o) => !o && setAccessTenant(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Acessos — {accessTenant?.legal_name}</DialogTitle>
            <DialogDescription>
              Compartilhe estes links conforme o tipo de pessoa que vai usar.
            </DialogDescription>
          </DialogHeader>
          {accessTenant && (
            <TenantAccessLinks
              slug={accessTenant.slug}
              admin_email={accessTenant.admin_email}
              legalName={accessTenant.legal_name}
              tenantId={accessTenant.id}
              canResetAdmin={true}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de exclusão (confirmação forte por slug) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="tenant-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-destructive">Excluir empresa permanentemente?</DialogTitle>
            <DialogDescription>
              Esta ação remove o acesso de <strong>{deleteTarget?.legal_name}</strong>, do admin e de toda a equipe.
              Os dados ficam preservados no banco para auditoria mas a empresa não conseguirá mais entrar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              ⚠️ Para confirmar, digite o <strong>slug</strong> da empresa:
              <code className="ml-1 px-1.5 py-0.5 rounded bg-destructive/10 font-mono">{deleteTarget?.slug}</code>
            </div>
            <Input
              data-testid="tenant-delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={deleteTarget?.slug || ""}
              autoFocus
              className="font-mono"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setConfirmText(""); }}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting || confirmText.trim().toLowerCase() !== (deleteTarget?.slug || "").toLowerCase()}
              data-testid="tenant-delete-confirm-btn"
              className="gap-2"
            >
              {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <Trash2 className="w-3.5 h-3.5" /> Excluir empresa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
